import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { 
  FiPlus, FiEdit2, FiTrash2, FiX, FiTag, FiPercent, FiDollarSign, 
  FiCalendar, FiCheckCircle, FiAlertCircle, FiRefreshCw, FiClock,
  FiUsers
} from 'react-icons/fi';
import styles from './AdminCoupons.module.css';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_value: 0,
    max_discount: '',
    valid_from: '',
    valid_to: '',
    usage_limit: '',
    per_customer_limit: 1,
    is_active: true
  });

  useEffect(() => {
    fetchCoupons();

    const subscription = supabase
      .channel('coupons-channel')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'coupons' }, () => {
        fetchCoupons();
      })
      .subscribe();

    return () => subscription.unsubscribe();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .order('created_at', { ascending: false });
    if (data && !error) {
      setCoupons(data);
    }
    setLoading(false);
  };

  const handleOpenModal = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_order_value: coupon.min_order_value || 0,
        max_discount: coupon.max_discount || '',
        valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
        valid_to: coupon.valid_to ? coupon.valid_to.split('T')[0] : '',
        usage_limit: coupon.usage_limit || '',
        per_customer_limit: coupon.per_customer_limit ?? 1,
        is_active: coupon.is_active
      });
    } else {
      setEditingCoupon(null);
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_value: 0,
        max_discount: '',
        valid_from: '',
        valid_to: '',
        usage_limit: '',
        per_customer_limit: 1,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const data = {
      code: formData.code.toUpperCase().trim(),
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      min_order_value: parseFloat(formData.min_order_value) || 0,
      max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
      valid_from: formData.valid_from || null,
      valid_to: formData.valid_to || null,
      usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
      per_customer_limit: parseInt(formData.per_customer_limit) || 1,
      is_active: formData.is_active
    };

    if (data.discount_value <= 0) {
      setMessage({ type: 'error', text: 'Discount value must be greater than 0' });
      setSaving(false);
      return;
    }

    let result;
    if (editingCoupon) {
      result = await supabase
        .from('coupons')
        .update({ ...data, updated_at: new Date() })
        .eq('id', editingCoupon.id);
    } else {
      result = await supabase
        .from('coupons')
        .insert([data]);
    }

    if (result.error) {
      if (result.error.code === '23505') {
        setMessage({ type: 'error', text: 'Coupon code already exists. Please use a different code.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save coupon: ' + result.error.message });
      }
    } else {
      setMessage({ type: 'success', text: `Coupon ${editingCoupon ? 'updated' : 'created'} successfully!` });
      fetchCoupons();
      setShowModal(false);
    }
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    const { error } = await supabase.from('coupons').delete().eq('id', id);
    if (error) {
      setMessage({ type: 'error', text: 'Failed to delete coupon' });
    } else {
      setMessage({ type: 'success', text: 'Coupon deleted!' });
      fetchCoupons();
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const toggleActive = async (id, current) => {
    const { error } = await supabase
      .from('coupons')
      .update({ is_active: !current, updated_at: new Date() })
      .eq('id', id);
    if (!error) fetchCoupons();
  };

  const getStatus = (coupon) => {
    const now = new Date();
    if (!coupon.is_active) return 'Inactive';
    if (coupon.valid_to && new Date(coupon.valid_to) < now) return 'Expired';
    if (coupon.usage_limit && coupon.used_count >= coupon.usage_limit) return 'Used Up';
    return 'Active';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.adminCoupons}>
      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
          {message.text}
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Coupon Management</h2>
          <p className={styles.headerSubtitle}>Create and manage discount coupons</p>
        </div>
        <button onClick={() => handleOpenModal()} className={styles.addBtn}>
          <FiPlus /> New Coupon
        </button>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#E8F5E9', color: '#1F5B3A' }}>
            <FiTag />
          </div>
          <div className={styles.statInfo}>
            <span>Total Coupons</span>
            <strong>{coupons.length}</strong>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#E3F2FD', color: '#3B82F6' }}>
            <FiCheckCircle />
          </div>
          <div className={styles.statInfo}>
            <span>Active</span>
            <strong>{coupons.filter(c => getStatus(c) === 'Active').length}</strong>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#FEE2E2', color: '#9E1B1B' }}>
            <FiClock />
          </div>
          <div className={styles.statInfo}>
            <span>Expired / Inactive</span>
            <strong>{coupons.filter(c => getStatus(c) !== 'Active').length}</strong>
          </div>
        </div>
      </div>

      <div className={styles.couponList}>
        {coupons.length === 0 ? (
          <div className={styles.emptyState}>
            <FiTag className={styles.emptyIcon} />
            <h3>No Coupons Yet</h3>
            <p>Create your first coupon to offer discounts to your customers.</p>
            <button onClick={() => handleOpenModal()} className={styles.emptyBtn}>
              <FiPlus /> Create Coupon
            </button>
          </div>
        ) : (
          <div className={styles.couponGrid}>
            {coupons.map(coupon => {
              const status = getStatus(coupon);
              const isActive = status === 'Active';

              return (
                <div key={coupon.id} className={`${styles.couponCard} ${!isActive ? styles.inactiveCard : ''}`}>
                  <div className={styles.couponHeader}>
                    <div className={styles.couponCode}>
                      <FiTag className={styles.codeIcon} />
                      <span>{coupon.code}</span>
                    </div>
                    <div className={styles.statusBadge} style={{
                      background: isActive ? '#D1FAE5' : status === 'Expired' ? '#FEE2E2' : '#FEF3C7',
                      color: isActive ? '#1F5B3A' : status === 'Expired' ? '#9E1B1B' : '#D97706'
                    }}>
                      {status}
                    </div>
                  </div>

                  <div className={styles.couponBody}>
                    <div className={styles.discountInfo}>
                      {coupon.discount_type === 'percentage' ? (
                        <span className={styles.discountValue}>{coupon.discount_value}% OFF</span>
                      ) : (
                        <span className={styles.discountValue}>₹{coupon.discount_value} OFF</span>
                      )}
                      {coupon.discount_type === 'percentage' && coupon.max_discount && (
                        <span className={styles.maxDiscount}> (max ₹{coupon.max_discount})</span>
                      )}
                    </div>

                    <div className={styles.couponDetails}>
                      {coupon.min_order_value > 0 && (
                        <div className={styles.detailRow}>
                          <FiDollarSign /> Min order: ₹{coupon.min_order_value}
                        </div>
                      )}
                      {coupon.valid_from && coupon.valid_to && (
                        <div className={styles.detailRow}>
                          <FiCalendar /> {new Date(coupon.valid_from).toLocaleDateString()} → {new Date(coupon.valid_to).toLocaleDateString()}
                        </div>
                      )}
                      {coupon.usage_limit && (
                        <div className={styles.detailRow}>
                          <FiRefreshCw /> {coupon.used_count || 0} / {coupon.usage_limit} used
                        </div>
                      )}
                      <div className={styles.detailRow}>
                        <FiUsers /> {coupon.per_customer_limit ?? 1} use(s) per customer
                      </div>
                    </div>

                    <div className={styles.couponActions}>
                      <label className={styles.toggle}>
                        <input
                          type="checkbox"
                          checked={coupon.is_active}
                          onChange={() => toggleActive(coupon.id, coupon.is_active)}
                        />
                        <span className={styles.toggleSlider}></span>
                      </label>
                      <button onClick={() => handleOpenModal(coupon)} className={styles.editBtn}>
                        <FiEdit2 />
                      </button>
                      <button onClick={() => handleDelete(coupon.id)} className={styles.deleteBtn}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className={styles.modal} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h3>
              <button onClick={() => setShowModal(false)} className={styles.closeBtn}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Coupon Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="E.g., WELCOME10"
                  required
                  className={styles.codeInput}
                  maxLength="20"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Discount Type *</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Discount Value *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    placeholder={formData.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                    required
                    min="0.01"
                  />
                </div>
              </div>

              {formData.discount_type === 'percentage' && (
                <div className={styles.formGroup}>
                  <label>Maximum Discount (₹) – Optional</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.max_discount}
                    onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                    placeholder="e.g., 500"
                    min="0"
                  />
                </div>
              )}

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Min Order Value (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.min_order_value}
                    onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Usage Limit (global)</label>
                  <input
                    type="number"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Per Customer Limit</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.per_customer_limit}
                  onChange={(e) => setFormData({ ...formData, per_customer_limit: parseInt(e.target.value) || 1 })}
                  placeholder="1"
                />
                <small style={{ color: '#7A6B5A', fontSize: '0.7rem' }}>
                  How many times this coupon can be used by the same customer. Default is 1.
                </small>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Valid From</label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Valid To</label>
                  <input
                    type="date"
                    value={formData.valid_to}
                    onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setShowModal(false)} className={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} className={styles.submitBtn}>
                  {saving ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;